import React, { useContext, useEffect, useState } from "react";
import {
  IonContent,
  IonLoading,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonViewWillEnter,
} from "@ionic/react";
import PageHeader from "../components/PageHeader";
import { TranslationsContext } from "../util/Translations";
import { getColumnsWithGroup } from "../api/Windows";
import { useQueryExec } from "../hooks/useQueryExec";
import QueryFilter from "../components/QueryFilter";
import ApprovalResults from "../components/ApprovalResults";
import { WorkOrderMetadata } from "../models/workorders/WorkOrder";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";

// US 806719 - Mobile PO Approvals Queue.
// The queue is driven off the query wizard: the user picks which Purchase Order
// approval query to run (defaulting to "Mobile PO Approvals"), and its rows are
// rendered as cards. Tapping a card opens the PO approval modal. Mirrors the
// query-picker pattern used by WorkOrders/Requisitions, layered over the shared
// ApprovalResults component used by WO/Req approvals.
const POApproval: React.FC = () => {
  const {
    showLoading,
    setShowLoading,
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    queryCriteria,
    getQueryCriteria,
    confirmSearch,
    queryInfo,
    queryName,
    criteriaParams,
    searchYn,
    setSearchYn,
    queryRows,
    executeSearch,
    errorState,
    totalCount,
    getNoOfFieldsToDisplay,
    execFilter,
    resultsFilter,
    filterRows,
  } = useQueryExec("PO Approve/Review", "mob_po_approvals", "cf_purchase_order");
  const { translations } = useContext(TranslationsContext);
  const [metadata, setMetadata] = useState<WorkOrderMetadata>();
  const querySection = "QW--PO Approve/Review";
  const approvalName = "PO";

  // Window metadata for the approval detail is fixed (mob_po_approvals) and does
  // not depend on which query the user runs, so load it once.
  useEffect(() => {
    const data = {
      WindowName: "mob_po_approvals",
      IncludeValidValues: true,
    };
    getColumnsWithGroup(data).then((response) => {
      setMetadata(response.data);
    });
  }, []);

  // Run the selected query when the user searches (confirmSearch sets searchYn).
  useEffect(() => {
    if (!searchYn) return;
    setShowLoading(true);
    getNoOfFieldsToDisplay("NoOfFieldsToDisplay");
    executeSearch(queryName, criteriaParams).finally(() => {
      setSearchYn(false);
      setShowLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchYn]);

  // Re-run the current query after an approval action or pull-to-refresh.
  const displayApprovalResults = async () => {
    if (!queryName) return;
    setShowLoading(true);
    getNoOfFieldsToDisplay("NoOfFieldsToDisplay");
    return executeSearch(queryName, criteriaParams).finally(() => {
      setShowLoading(false);
    });
  };

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    displayApprovalResults().finally(() => event.detail.complete());
  };

  // Re-run the current query whenever the queue is re-entered - e.g. after
  // approving/reviewing/rejecting a PO on the routed detail screen, which sends
  // the user back here. Without this the processed PO would linger in the list
  // (WO/Req refresh via the ApprovalResults modal flow; PO uses a routed page).
  // Guarded on queryName so the initial entry, before any search, is a no-op.
  useIonViewWillEnter(() => {
    if (queryName) {
      displayApprovalResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryName, criteriaParams]);

  return (
    <IonPage>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <PageHeader
        title={translations["lbl_mob_po_approval"] || "Purchase Order Approval"}
      ></PageHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <QueryFilter
          setShowLoading={setShowLoading}
          getQueryList={getQueryList}
          queryList={queryList}
          querySection={querySection}
          getFilterParams={getFilterParams}
          filterParams={filterParams}
          setParameter={setParameter}
          queryCriteria={queryCriteria}
          getQueryCriteria={getQueryCriteria}
          searchCallback={confirmSearch}
          queryInfo={queryInfo}
          triggerId="po-approval-popover-trigger"
          enableInlineDropdowns={true}
          preserveQueryOnReenter={true}
        />
        {!!metadata && (
          <ApprovalResults
            queryRows={queryRows}
            errorState={errorState}
            totalCount={totalCount}
            showLoading={showLoading}
            setShowLoading={setShowLoading}
            filterMethod={execFilter}
            filterString={resultsFilter}
            filterRows={filterRows}
            metadata={metadata}
            displayApprovalResults={displayApprovalResults}
            approvalName={approvalName}
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(reactPlugin, POApproval, "POApproval");
